/* ============================================================================
   ReportCardModal / index.jsx
   Boletim do portal do responsável — RÉPLICA do boletim impresso da escola
   (mesmo layout do GradeIstquarter), em formato modal.

   Uso no dashboard:
     import ReportCardModal from '../../components/ReportCardModal'
     const [rcOpen, setRcOpen] = useState(false)
     <ReportCardModal
       open={rcOpen}
       onClose={() => setRcOpen(false)}
       studentId={studentId}
       studentName={student?.name}
       schoolName={schoolName(student)}
       serie={...}      // opcional
       turma={...}      // opcional
       logoUrl={...}    // opcional; senao busca no localStorage/S3
       idSchool={...}   // opcional; para cache da logo
       format={assessmentFormatOf(student)}   // 'grade' | 'concept'
     />

   Fonte de dados: getReportCardsByStudentAndYear(studentId, year)
     → { data: [ ReportCard(por bimestre) ], finalConcepts: [{matter,concept}], message }

   Nota Total  = soma dos totalGrade dos 4 bimestres
   Nota Média  = soma dos averageGrade dos 4 bimestres
   ========================================================================== */

   import React, { useState, useEffect, useRef } from 'react'
   import { IoCheckmarkSharp, IoCloseSharp, IoClose } from 'react-icons/io5'
   import { FcSurvey } from 'react-icons/fc'
   //import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
   import { getReportCardsByStudentAndYear, fetchLogo, GetMatter, getSchoolYear } from '../../Api'
   import { NotaComRec, NotaComRecF, LegendaNotas, useRecuperacoes } from '../Recuperacao'
   import { useRegimeDoAno } from '../../utils/regimeDoAno'
   import {
       Overlay, ModalCard, TopBar, CloseButton, PrintButton, Body, Loading, Empty,
       ContLogo, Preview, DadosStdt, UpContainer, InfoContainer,
       SpanFrequency, LegendBox, DivDados, List, Emp, DivNameMatter, SpanNameMatter,
       Grade, DivBimTable, DivBimRow, DivBimHeader, DivBimCell,
       NotesHeader, ScaleCol, ScaleColTitle, ScaleAnnual, ScalePeriods, SpanTotalGrade, SpanAverageGrade, LegendColors,
       DivSignatureArea, SignatureBlock, Line, PrintIsolation,
   } from './styles'
   
   /* ---------- helpers --------------------------------------------------------- */
   
   const EF_NAME = 'EDUCAÇÃO FÍSICA'
   const norm = (s = '') => String(s).trim().toUpperCase()
   
   const bimIndex = (b) => {
       const m = String(b || '').match(/\d+/)
       return m ? parseInt(m[0], 10) - 1 : 99
   }
   
   const toNumber = (v) => {
       if (v == null || v === '') return NaN
       if (typeof v === 'number') return v
       return parseFloat(String(v).replace(',', '.'))
   }
   
   // extrai o valor exibível de uma célula do studentGrade.
   // Estruturas possíveis (dependem do regime, como gravado no ReportCard):
   //   NOTA     → { total: número, atividades: [...] }  → usa .total
   //   CONCEITO → "B"  ou  "B, A"                        → usa a string (1º conceito)
   //   histórico→ { value, fromHistory:true }            → usa .value
   const rawCellValue = (cell, format) => {
       if (cell == null) return ''
       if (typeof cell === 'object') {
           if ('total' in cell) return cell.total          // nota
           if ('value' in cell) return cell.value          // histórico
           return ''
       }
       // conceito: pode vir "B, A" (concatenado); pega o primeiro conceito
       if (format === 'concept') {
           const first = String(cell).split(',')[0]
           return first.trim()
       }
       return cell
   }
   
   // histórico (outra escola): marcado como fromHistory OU todas as notas sem atividade (igual ao boletim da turma)
   const isHistoryCell = (cell) => {
       if (!cell || typeof cell !== 'object') return false
       if (cell.fromHistory) return true
       const ats = Array.isArray(cell.atividades) ? cell.atividades : []
       return ats.length > 0 && ats.every(a => !a || !a.idActivity)
   }

   // recuperação do período já aplicada no boletim: { original, final } | null
   const recDaCelula = (cell) => {
       if (!cell || typeof cell !== 'object') return null
       const r = toNumber(cell.recuperacao)
       if (isNaN(r)) return null
       const original = toNumber(cell.totalOriginal)
       return { original: isNaN(original) ? toNumber(cell.total) : original, final: toNumber(cell.total) }
   }
   
   // pivota os ReportCard (um por bimestre) → linhas por matéria
   const buildRows = (cards, finalConcepts, format, matterOrder, regimeAno = '') => {
       const list = Array.isArray(cards) ? cards : []
       const isConcept = format === 'concept'
   
       // conceito final por matéria
       const finalMap = new Map()
       for (const fc of (Array.isArray(finalConcepts) ? finalConcepts : [])) {
           const k = norm(fc?.matter)
           if (k) finalMap.set(k, (fc?.concept || '').trim())
       }
   
       // detecta o regime a partir do 1º PERÍODO (menor bimonthly), lendo o
       // assessmentRegime do quarter populado desse card. Bimestral → "Bim"/4;
       // trimestral → "Trim"/3.
       let firstCard = null
       let firstIdx = Infinity
       for (const c of list) {
           const idx = bimIndex(c?.bimonthly)
           if (idx >= 0 && idx < firstIdx) { firstIdx = idx; firstCard = c }
       }
       const regimeOf = (c) => {
           const q = c?.id_iStQuarter || c?.id_iiNdQuarter || c?.id_iiiRdQuarter || c?.id_ivThQuarter
           return (q && typeof q === 'object' ? (q.assessmentRegime || '') : '')
       }
       let regime = String(regimeOf(firstCard) || '').trim().toUpperCase()
       // regime do ANO (vem da escola); sem ele, usa o que vier nos dados
       if (regimeAno) regime = String(regimeAno).trim().toUpperCase()
       const isTrimestral = regime === 'TRIMESTRAL'
       const periodLabel = isTrimestral ? 'Trim' : 'Bim'
       const periodLabelFull = isTrimestral ? 'Trimestre' : 'Bimestre'
       const maxIdx = isTrimestral ? 2 : 3   // trimestral vai até o 3º período
   
       // escala (totalGrade/averageGrade) POR período — usada para colorir a
       // nota de cada bimestre com a média/total daquele mesmo bimestre
       const scaleByIdx = {}
       for (const c of list) {
           const idx = bimIndex(c?.bimonthly)
           if (idx < 0 || idx > maxIdx) continue
           scaleByIdx[idx] = {
               total: toNumber(c?.totalGrade),
               average: toNumber(c?.averageGrade),
           }
       }
   
       // períodos presentes (respeitando o limite do regime)
       const periodsSet = new Set()
       for (const c of list) {
           const idx = bimIndex(c?.bimonthly)
           if (idx >= 0 && idx <= maxIdx) periodsSet.add(idx)
       }
       const periods = [...periodsSet].sort((a, b) => a - b)
   
       // matéria → { matter, cells: {idx: valorExibivel}, history: {idx: bool} }
       const matterMap = new Map()
       for (const c of list) {
           const idx = bimIndex(c?.bimonthly)
           if (idx < 0 || idx > maxIdx) continue
           const sg = c?.studentGrade || {}
           const entries = sg instanceof Map ? [...sg.entries()] : Object.entries(sg)
           for (const [matter, value] of entries) {
               const k = norm(matter)
               if (!matterMap.has(k)) matterMap.set(k, { matter, cells: {}, history: {}, recs: {} })
               matterMap.get(k).cells[idx] = rawCellValue(value, format)
               matterMap.get(k).history[idx] = isHistoryCell(value)
               matterMap.get(k).recs[idx] = format === 'concept' ? null : recDaCelula(value)
           }
       }
   
       // ordena as matérias seguindo a lista de referência (GetMatter da escola),
       // Educação Física sempre no fim — idêntico ao sortByUserOrder do boletim.
       // Matérias fora da lista de referência vão ao final (mas antes/junto de EF).
       const orderIndex = new Map(
           (Array.isArray(matterOrder) ? matterOrder : [])
               .map((m, i) => [norm(typeof m === 'string' ? m : (m?.name || '')), i])
       )
       const BIG = Number.MAX_SAFE_INTEGER
       const rankOf = (matterName) => {
           const k = norm(matterName)
           if (k === EF_NAME) return BIG            // EF sempre por último
           return orderIndex.has(k) ? orderIndex.get(k) : BIG - 1  // desconhecidas antes da EF
       }
       const rows = [...matterMap.values()].sort((a, b) => {
           const ra = rankOf(a.matter)
           const rb = rankOf(b.matter)
           if (ra !== rb) return ra - rb
           // empate (ex.: ambas fora da lista): alfabético estável
           return a.matter.localeCompare(b.matter, 'pt-BR')
       })
   
       // coluna final: nota → soma dos bimestres; conceito → FinalConcepts
       for (const row of rows) {
           if (isConcept) {
               row.finalValue = finalMap.get(norm(row.matter)) || ''
           } else {
               let sum = 0, any = false
               for (const v of Object.values(row.cells)) {
                   const n = toNumber(v)
                   if (!isNaN(n)) { sum += n; any = true }
               }
               row.finalValue = any ? Math.round(sum * 10) / 10 : ''
           }
       }
       const hasFinalColumn = isConcept
           ? rows.some(r => r.finalValue)
           : rows.some(r => r.finalValue !== '' && r.finalValue != null)
   
       // Nota Total / Média = SOMA dos totalGrade / averageGrade dos bimestres.
       // A anual só é válida se TODOS os períodos do regime existirem
       // (4 bimestres OU 3 trimestres) e cada um trouxer sua escala.
       const expectedPeriods = maxIdx + 1   // bimestral → 4, trimestral → 3
       let totalSum = 0, avgSum = 0
       let periodsWithTotal = 0, periodsWithAverage = 0
       for (const idx of periods) {
           const sc = scaleByIdx[idx] || {}
           if (!isNaN(sc.total)) { totalSum += sc.total; periodsWithTotal++ }
           if (!isNaN(sc.average)) { avgSum += sc.average; periodsWithAverage++ }
       }
       // exige que todos os períodos esperados estejam presentes com escala
       const totalComplete = periods.length === expectedPeriods && periodsWithTotal === expectedPeriods
       const avgComplete = periods.length === expectedPeriods && periodsWithAverage === expectedPeriods
       const hasScale = totalComplete || avgComplete
       const totalGrade = totalComplete ? Math.round(totalSum * 10) / 10 : NaN
       const averageGrade = avgComplete ? Math.round(avgSum * 10) / 10 : NaN
   
       // frequência anual (soma dos bimestres)
       const freq = { presencas: 0, faltas: 0, faltasJust: 0 }
       for (const c of list) {
           const f = c?.frequencia || {}
           freq.presencas += f.totalPresencas || 0
           freq.faltas += f.totalFaltas || 0
           freq.faltasJust += f.totalFaltasJustificadas || 0
       }
   
       // metadados do cabeçalho — vêm do ÚLTIMO boletim da lista (o mais recente),
       // lendo o idClass populado (serie, turma, level, shift) e a escola.
       const last = list.length ? list[list.length - 1] : {}
       const cls = (last?.idClass && typeof last.idClass === 'object') ? last.idClass : {}
       const q = (last?.id_iStQuarter && typeof last.id_iStQuarter === 'object') ? last.id_iStQuarter : {}
   
       // id da escola: tenta várias origens possíveis do payload
       const schoolId =
           (last?.id_school && typeof last.id_school === 'object' ? last.id_school._id : last?.id_school) ||
           cls?.id_school || q?.id_school || null
   
       const meta = {
           nameTeacher: (last.nameTeacher || '').trim(),
           nameSchool: (last.nameSchool || '').trim(),
           year: (last.year || cls.year || '').toString().trim(),
           serie: (cls.serie || '').toString().trim(),
           level: (cls.level || '').toString().trim(),
           turma: (cls.name || cls.name_class || '').toString().trim(),
           shift: (cls.shift || '').toString().trim(),
           schoolId,
       }
   
       return { periods, rows, hasFinalColumn, totalGrade, averageGrade, hasScale, freq, meta, periodLabel, periodLabelFull, isTrimestral, scaleByIdx }
   }
   
   const fmtNota = (v) => {
       const n = toNumber(v)
       if (isNaN(n)) return v == null || v === '' ? 'N/A' : String(v)
       return n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })
   }
   
   /* ---------- componente ------------------------------------------------------ */
   
   const ReportCardModal = ({
       open,
       onClose,
       studentId,
       studentName,
       schoolName,
       serie,
       turma,
       logoUrl: logoUrlProp,
       idSchool,
       format = 'grade',
   }) => {
       const [year, setYear] = useState()
       // ano letivo atual da escola — teto do seletor de anos
       const [anoLetivoAtual, setAnoLetivoAtual] = useState(new Date().getFullYear())
       const [cards, setCards] = useState([])
       const [finalConcepts, setFinalConcepts] = useState([])
       const [matterOrder, setMatterOrder] = useState([])
       const [loading, setLoading] = useState(false)
       const [logoUrl, setLogoUrl] = useState(logoUrlProp || '')
       const cancelled = useRef(false)
   
       const isConcept = format === 'concept'
   
       // ao abrir: define o ano inicial como o ANO LETIVO da escola (do backend);
       // se falhar, cai no ano atual. Usa a prop idSchool ou o sessionStorage
       // 'id-school' (mesma origem do restante do app) — disponível já na abertura.
       useEffect(() => {
           if (!open) return
           let active = true
   
           const resolveInitialSchoolId = () => {
               if (idSchool) return idSchool
               try {
                   const s = JSON.parse(sessionStorage.getItem('id-school'))
                   if (s) return s
               } catch (e) { /* ignora */ }
               return null
           }
   
           ;(async () => {
               const sid = resolveInitialSchoolId()
               if (!sid) { if (active) { setYear(new Date().getFullYear()); setAnoLetivoAtual(new Date().getFullYear()) } return }
               try {
                   const res = await getSchoolYear(sid)
                   if (!active) return
                   const y = parseInt(res?.data?.data, 10)
                   const anoValido = !isNaN(y) ? y : new Date().getFullYear()
                   setYear(anoValido)
                   setAnoLetivoAtual(anoValido) // teto do seletor
               } catch (e) {
                   if (active) { setYear(new Date().getFullYear()); setAnoLetivoAtual(new Date().getFullYear()) }
               }
           })()
   
           return () => { active = false }
       }, [open, idSchool])
   
       // busca boletins + conceitos finais
       // (só busca depois de saber o ano; cada busca tem a sua própria trava, então uma resposta
       //  antiga — ex.: a do ano ainda indefinido — nunca apaga o boletim já carregado)
       useEffect(() => {
           if (!open || !studentId) return
           setLoading(true)
           if (!year) return // aguarda o ano letivo
           let ativo = true
           cancelled.current = false
           const load = async () => {
               try {
                   const res = await getReportCardsByStudentAndYear(studentId, year)
                   if (!ativo) return
                   setCards(res?.data?.data || [])
                   setFinalConcepts(res?.data?.finalConcepts || [])
               } catch (err) {
                   if (!ativo) return
                   setCards([])
                   setFinalConcepts([])
               } finally {
                   if (ativo) setLoading(false)
               }
           }
           load()
           return () => { ativo = false; cancelled.current = true }
       }, [open, year, studentId])

       // recuperação final (anual) do aluno no ano exibido
       const { recFinalAno } = useRecuperacoes({ id_student: open ? studentId : null, year })
   
       // resolve o id da escola: prop tem prioridade; senão do card carregado;
       // senão do sessionStorage 'id-school' (mesma fonte usada no boletim original)
       const resolvedSchoolId = idSchool || (() => {
           const c = Array.isArray(cards) && cards.length ? cards[0] : null
           if (c) {
               const s = c.id_school
               if (s && typeof s === 'object' && s._id) return s._id
               if (s) return s
               const q = c.id_iStQuarter
               if (q && typeof q === 'object' && q.id_school) return q.id_school
               const cl = c.idClass
               if (cl && typeof cl === 'object' && cl.id_school) return cl.id_school
           }
           try {
               const fromSession = JSON.parse(sessionStorage.getItem('id-school'))
               if (fromSession) return fromSession
           } catch (e) { /* ignora parse inválido */ }
           return null
       })()

       // regime de avaliação do ANO exibido (bimestre ou trimestre)
       const regimeAno = useRegimeDoAno(open ? resolvedSchoolId : null, year)
   
       // ordem das matérias — GetMatter(idSchool), Educação Física no fim
       // (mesma fonte/ordenação do boletim original)
       useEffect(() => {
           if (!open || !resolvedSchoolId) return
           let active = true
               ; (async () => {
                   try {
                       const res = await GetMatter(resolvedSchoolId)
                       if (!active) return
                       const raw = res?.data?.data || []
                       const ordered = [...raw].sort((a, b) => {
                           const aEF = norm(a?.name) === EF_NAME
                           const bEF = norm(b?.name) === EF_NAME
                           if (aEF) return 1
                           if (bEF) return -1
                           return 0
                       })
                       setMatterOrder(ordered)
                   } catch (e) { /* silencioso: sem ordem, cai no fallback alfabético */ }
               })()
           return () => { active = false }
       }, [open, resolvedSchoolId])
   
       // logo: usa a prop, senão cache local, senão S3 (mesmo padrão do boletim original)
       useEffect(() => {
           if (!open) return
           if (logoUrlProp) { setLogoUrl(logoUrlProp); return }
           if (!resolvedSchoolId) return
   
           const cached = localStorage.getItem(`school-logo-${resolvedSchoolId}`)
           if (cached) { setLogoUrl(cached); return }
   
           let active = true
               ; (async () => {
                   try {
                       const logoRes = await fetchLogo(resolvedSchoolId)
                       if (active && logoRes?.url) {
                           setLogoUrl(logoRes.url)
                           localStorage.setItem(`school-logo-${resolvedSchoolId}`, logoRes.url)
                           if (logoRes._id) localStorage.setItem(`school-logo-id-${resolvedSchoolId}`, logoRes._id)
                       }
                   } catch (e) { /* silencioso: logo é opcional */ }
               })()
           return () => { active = false }
       }, [open, logoUrlProp, resolvedSchoolId])
   
       if (!open) return null
   
       const data = buildRows(cards, finalConcepts, format, matterOrder, regimeAno)
       const hasData = data.rows.length > 0

       // Impressão isolada: marca o <body> com uma classe enquanto imprime.
       // O CSS (styles.js) usa essa classe para esconder TODA a página e
       // exibir apenas o boletim — assim nada do dashboard vai para o papel.
       const handlePrint = () => {
           const cls = 'printing-report-card'
           const cleanup = () => {
               document.body.classList.remove(cls)
               window.removeEventListener('afterprint', cleanup)
           }
           document.body.classList.add(cls)
           window.addEventListener('afterprint', cleanup)
           window.print()
           // fallback p/ navegadores que nao disparam afterprint de forma confiavel
           setTimeout(cleanup, 1000)
       }
   
       return (
           <Overlay id="report-card-print-root" onClick={onClose}>
               <PrintIsolation />
               <ModalCard onClick={(e) => e.stopPropagation()}>
                   <TopBar>
                       <style>{`@media print { .rc-year-selector { display: none !important; } }`}</style>
                       <label className="rc-year-selector" style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 'auto' }}>
                           <span style={{ fontWeight: 700, fontSize: 14 }}>Ano Letivo:</span>
                           <select
                               value={year || anoLetivoAtual}
                               onChange={(e) => setYear(Number(e.target.value))}
                               style={{
                                   padding: '6px 10px',
                                   borderRadius: 8,
                                   border: '1.5px solid var(--line)',
                                   fontSize: 14,
                                   fontWeight: 600,
                                   fontFamily: 'inherit',
                                   color: 'var(--teal-text, var(--teal))',
                                   background: 'var(--surface)',
                                   cursor: 'pointer',
                                   outline: 'none'
                               }}
                           >
                               {Array.from({ length: 6 }, (_, i) => anoLetivoAtual - i).map((y) => (
                                   <option key={y} value={y}>
                                       {y}{y === anoLetivoAtual ? ' (atual)' : ''}
                                   </option>
                               ))}
                           </select>
                       </label>
                       <PrintButton onClick={handlePrint}>Imprimir</PrintButton>
                       <CloseButton onClick={onClose} aria-label="Fechar">
                           <IoClose size={20} />
                       </CloseButton>
                   </TopBar>
   
                   <Body id="containerDivs">
                       {loading ? (
                           <Loading>Carregando boletim…</Loading>
                       ) : !hasData ? (
                           <Empty>Ainda não há boletim lançado para {year}.</Empty>
                       ) : (
                           <>
                               <ContLogo>
                                   {logoUrl && <Preview src={logoUrl} alt="Logo da escola" />}
                                   <h2>Boletim</h2>
                               </ContLogo>
   
                               <DadosStdt>
                                   <UpContainer>
                                       <InfoContainer>
                                           <span><strong>Escola:</strong> {data.meta.nameSchool || schoolName || '—'}</span>
                                           {(data.meta.serie || serie) && (
                                               <span>
                                                   <strong>Série:</strong> {data.meta.serie || serie}
                                                   {data.meta.level ? ` - ${data.meta.level}` : ''}
                                               </span>
                                           )}
                                           {(data.meta.turma || turma) && (
                                               <span><strong>Turma:</strong> {data.meta.turma || turma}</span>
                                           )}
                                           <span><strong>Ano Letivo:</strong> {data.meta.year || year}</span>
                                           <span><strong>Professor(a):</strong> {data.meta.nameTeacher || '—'}</span>
                                           <span><strong>Aluno(a):</strong> {studentName || data.meta.nameStudent || '—'}</span>
                                       </InfoContainer>
                                       <SpanFrequency>
                                           <span>
                                               <IoCheckmarkSharp color="#00fa00" size={18} />
                                               Presenças: {data.freq.presencas} |
                                               <IoCloseSharp color="#ff050a" size={18} />
                                               Faltas: {data.freq.faltas}
                                           </span>
                                           <span>
                                               <FcSurvey size={16} />
                                               Faltas Justificadas: {data.freq.faltasJust}
                                           </span>
                                       </SpanFrequency>
                                   </UpContainer>
   
                                   {isConcept ? (
                                       <LegendBox style={{ display: "block" }}>
                                           <h3>Legenda</h3>
                                           <p><strong style={{ color: 'var(--green)' }}>A</strong> - Alcançou com êxito as capacidades básicas</p>
                                           <p><strong style={{ color: 'blue' }}>B</strong> - Alcançou satisfatoriamente as capacidades básicas</p>
                                           <p><strong style={{ color: 'orange' }}>C</strong> - Alcançou parcialmente as capacidades básicas</p>
                                           <p><strong style={{ color: 'red' }}>D</strong> - Não alcançou as capacidades básicas</p>
                                       </LegendBox>
                                   ) : (
                                       <NotesHeader>
                                           {/* ESQUERDA — total/média de cada bimestre, empilhados */}
                                           {data.periods.some(idx => data.scaleByIdx[idx] && (!isNaN(data.scaleByIdx[idx].total) || !isNaN(data.scaleByIdx[idx].average))) && (
                                               <ScaleCol>
                                                   <ScaleColTitle>Por {data.periodLabelFull.toLowerCase()}</ScaleColTitle>
                                                   <ScalePeriods>
                                                       {data.periods.map((idx) => {
                                                           const sc = data.scaleByIdx[idx] || {}
                                                           if (isNaN(sc.total) && isNaN(sc.average)) return null
                                                           return (
                                                               <span key={idx}>
                                                                   <strong>{idx + 1}º {data.periodLabel}:</strong>{' '}
                                                                   Total <SpanTotalGrade>{isNaN(sc.total) ? '—' : fmtNota(sc.total)}</SpanTotalGrade>
                                                                   {' · '}
                                                                   Média <SpanAverageGrade>{isNaN(sc.average) ? '—' : fmtNota(sc.average)}</SpanAverageGrade>
                                                               </span>
                                                           )
                                                       })}
                                                   </ScalePeriods>
                                               </ScaleCol>
                                           )}
   
                                           {/* MEIO — total/média anual, empilhados */}
                                           {data.hasScale && (
                                               <ScaleCol>
                                                   <ScaleColTitle>Anual</ScaleColTitle>
                                                   <ScaleAnnual>
                                                       <span>Nota Total: <SpanTotalGrade>{fmtNota(data.totalGrade)}</SpanTotalGrade></span>
                                                       <span>Nota Média: <SpanAverageGrade>{fmtNota(data.averageGrade)}</SpanAverageGrade></span>
                                                   </ScaleAnnual>
                                               </ScaleCol>
                                           )}
   
                                           {/* DIREITA — legenda (mesma dos boletins da escola) */}
                                           <LegendColors style={{ alignItems: 'center' }}>
                                               <LegendaNotas recFinal />
                                           </LegendColors>
                                       </NotesHeader>
                                   )}
                               </DadosStdt>
   
                               <DivDados>
                                   <List>
                                       {data.rows.map((row) => (
                                           <Emp key={row.matter}>
                                               <DivNameMatter>
                                                   <SpanNameMatter>{row.matter}</SpanNameMatter>
                                               </DivNameMatter>
                                               <Grade>
                                                   <DivBimTable>
                                                       {data.periods.map((idx) => {
                                                           const raw = row.cells[idx]
                                                           const rec = row.recs?.[idx] || null
                                                           const value = isConcept
                                                               ? (raw == null || raw === '' ? 'N/A' : String(raw).trim())
                                                               : (rec ? <NotaComRec nota={rec.original} rec={rec.final} virgula /> : fmtNota(raw))
                                                           // cor da nota usa a média/total DESTE bimestre (nota do período, sem a recuperação)
                                                           const sc = data.scaleByIdx[idx] || {}
                                                           return (
                                                               <DivBimRow key={idx}>
                                                                   <DivBimHeader>{idx + 1}º {data.periodLabel}</DivBimHeader>
                                                                   <DivBimCell
                                                                       $format={format}
                                                                       $grade={rec ? rec.original : raw}
                                                                       $average={sc.average}
                                                                       $total={sc.total}
                                                                       $history={!!row.history?.[idx]}
                                                                   >
                                                                       {value}
                                                                   </DivBimCell>
                                                               </DivBimRow>
                                                           )
                                                       })}
                                                       {data.hasFinalColumn && (
                                                           <DivBimRow>
                                                               <DivBimHeader>{isConcept ? 'Final' : 'Total'}</DivBimHeader>
                                                               <DivBimCell
                                                                   className="final"
                                                                   $format={format}
                                                                   $grade={row.finalValue}
                                                                   /* nota: Total (soma) colore contra a escala ANUAL
                                                                      (soma dos totais/médias). conceito: cor vem da
                                                                      letra da legenda; média/total são ignorados. */
                                                                   $average={isConcept ? undefined : data.averageGrade}
                                                                   $total={isConcept ? undefined : data.totalGrade}
                                                               >
                                                                   {isConcept
                                                                       ? (row.finalValue || 'N/A')
                                                                       : (row.finalValue === '' || row.finalValue == null
                                                                           ? fmtNota(row.finalValue)
                                                                           : <NotaComRecF nota={row.finalValue} recF={recFinalAno(null, row.matter, row.finalValue)} virgula />)}
                                                               </DivBimCell>
                                                           </DivBimRow>
                                                       )}
                                                   </DivBimTable>
                                               </Grade>
                                           </Emp>
                                       ))}
                                   </List>
                               </DivDados>
   
                               <DivSignatureArea>
                                   <SignatureBlock>
                                       <Line />
                                       <span>Assinatura do Professor</span>
                                   </SignatureBlock>
                                   <SignatureBlock>
                                       <Line />
                                       <span>Assinatura dos Pais ou Responsável</span>
                                   </SignatureBlock>
                               </DivSignatureArea>
                           </>
                       )}
                   </Body>
               </ModalCard>
           </Overlay>
       )
   }
   
   export default ReportCardModal